/**
 * A origem da venda, gravada no próprio sistema (OS Medição, item 6): de onde a pessoa veio na
 * primeira visita e o que a trouxe ao checkout na visita em que comprou. Vai no corpo do checkout
 * e o servidor grava junto com o pedido, sem depender do GA4.
 *
 * - Primeira visita: cookie próprio de 30 dias (`bella_origem`), gravado uma vez só.
 * - Visita de conversão: sessionStorage da aba, com as UTMs da entrada e o último `acionador`.
 *
 * Nada de dado pessoal: a página vai só com o caminho (sem query) e qualquer valor com cara de
 * e-mail ou CPF é descartado. Todo acesso a cookie e storage fica em try/catch: navegador que
 * bloqueia storage não pode quebrar a página nem o checkout.
 */

export const COOKIE_DA_PRIMEIRA_VISITA = "bella_origem";
export const CHAVE_DA_CONVERSAO = "bella_origem_conversao";
export const DIAS_DO_COOKIE = 30;
export const TAMANHO_MAXIMO = 150;

const UTMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

type Utm = (typeof UTMS)[number];

export type Query = Record<string, unknown>;

export type OrigemPrimeira = Partial<Record<Utm | "pagina" | "em", string>>;
export type OrigemConversao = Partial<Record<Utm | "acionador" | "pagina", string>>;

const PARECE_EMAIL = /@/;
// 11 dígitos seguidos, ou no formato 000.000.000-00.
const PARECE_CPF = /(^|\D)(\d{11}|\d{3}\.\d{3}\.\d{3}-\d{2})(\D|$)/;

/** Texto curto e sem dado pessoal; qualquer outra coisa vira undefined. */
export function limparValor(valor: unknown): string | undefined {
  if (Array.isArray(valor)) valor = valor[0];
  if (typeof valor !== "string" && typeof valor !== "number") return undefined;

  const texto = String(valor).trim();
  if (!texto || PARECE_EMAIL.test(texto) || PARECE_CPF.test(texto)) return undefined;

  return texto.slice(0, TAMANHO_MAXIMO);
}

/** Só o caminho: a query pode trazer e-mail ou token. */
export function soCaminho(caminho: unknown): string | undefined {
  if (typeof caminho !== "string") return undefined;
  return limparValor(caminho.split("?")[0].split("#")[0]);
}

/** Só as chaves com valor, para o corpo não levar campo vazio. */
function semVazios<T extends Record<string, string | undefined>>(objeto: T): Partial<T> {
  const limpo: Partial<T> = {};
  for (const chave of Object.keys(objeto) as (keyof T)[]) {
    if (objeto[chave] !== undefined) limpo[chave] = objeto[chave];
  }
  return limpo;
}

function utmsDa(query: Query): Partial<Record<Utm, string>> {
  const utms: Partial<Record<Utm, string | undefined>> = {};
  for (const utm of UTMS) utms[utm] = limparValor(query[utm]);
  return utms as Partial<Record<Utm, string>>;
}

export function montarPrimeira(query: Query, caminho: string, agora: Date): OrigemPrimeira {
  return semVazios({ ...utmsDa(query), pagina: soCaminho(caminho), em: agora.toISOString() });
}

/**
 * A visita de conversão: as UTMs e a página de entrada ficam as da primeira página da aba; o
 * `acionador` é o último que apareceu na URL (o botão que levou ao checkout).
 */
export function atualizarConversao(atual: OrigemConversao | null, query: Query, caminho: string): OrigemConversao {
  const base: OrigemConversao = atual ?? semVazios({ ...utmsDa(query), pagina: soCaminho(caminho) });
  const acionador = limparValor(query.acionador);

  return acionador ? { ...base, acionador } : base;
}

/** Os dois blocos no formato do contrato do checkout, só quando têm algo. */
export function paraCorpo(primeira: OrigemPrimeira | null, conversao: OrigemConversao | null) {
  const corpo: { origem_primeira?: OrigemPrimeira; origem_conversao?: OrigemConversao } = {};
  if (primeira && Object.keys(primeira).length) corpo.origem_primeira = primeira;
  if (conversao && Object.keys(conversao).length) corpo.origem_conversao = conversao;
  return corpo;
}

/** Relê o que foi guardado, passando de novo pela limpeza: o cookie pode ter sido mexido. */
export function relerOrigem<T extends Record<string, string | undefined>>(bruto: string | null | undefined): Partial<T> | null {
  if (!bruto) return null;
  try {
    const lido = JSON.parse(bruto);
    if (!lido || typeof lido !== "object" || Array.isArray(lido)) return null;
    const limpo: Record<string, string> = {};
    for (const [chave, valor] of Object.entries(lido)) {
      const texto = chave === "pagina" ? soCaminho(valor) : limparValor(valor);
      if (texto !== undefined) limpo[chave] = texto;
    }
    return limpo as Partial<T>;
  } catch (_e) {
    return null;
  }
}

// --- Navegador -------------------------------------------------------------------------------

function lerCookie(nome: string): string | null {
  try {
    const par = document.cookie.split("; ").find((c) => c.startsWith(nome + "="));
    return par ? decodeURIComponent(par.slice(nome.length + 1)) : null;
  } catch (_e) {
    return null;
  }
}

function gravarCookie(nome: string, valor: string, dias: number) {
  try {
    const expira = new Date(Date.now() + dias * 864e5).toUTCString();
    document.cookie = `${nome}=${encodeURIComponent(valor)}; expires=${expira}; path=/; SameSite=Lax`;
  } catch (_e) {
    // Cookie bloqueado: a venda segue sem a origem da primeira visita
  }
}

function lerSessao(chave: string): string | null {
  try {
    return sessionStorage.getItem(chave);
  } catch (_e) {
    return null;
  }
}

function gravarSessao(chave: string, valor: string) {
  try {
    sessionStorage.setItem(chave, valor);
  } catch (_e) {
    // Storage bloqueado: a venda segue sem a origem da conversão
  }
}

/** Chamado a cada página, só no cliente. */
export function registrarPaginaDaVisita(query: Query, caminho: string, agora: Date = new Date()) {
  if (typeof window === "undefined") return;

  if (!relerOrigem(lerCookie(COOKIE_DA_PRIMEIRA_VISITA))) {
    gravarCookie(COOKIE_DA_PRIMEIRA_VISITA, JSON.stringify(montarPrimeira(query, caminho, agora)), DIAS_DO_COOKIE);
  }

  const conversao = relerOrigem<OrigemConversao>(lerSessao(CHAVE_DA_CONVERSAO));
  gravarSessao(CHAVE_DA_CONVERSAO, JSON.stringify(atualizarConversao(conversao, query, caminho)));
}

/** O que vai no corpo do checkout. */
export function origemDaVisita() {
  if (typeof window === "undefined") return {};

  return paraCorpo(
    relerOrigem<OrigemPrimeira>(lerCookie(COOKIE_DA_PRIMEIRA_VISITA)),
    relerOrigem<OrigemConversao>(lerSessao(CHAVE_DA_CONVERSAO))
  );
}
